import dayjs from 'dayjs';
import { rangeValues } from './healthCalorieGoalCalc';

export const INBODY_SCAN_HP_REWARD = 2;

const INBODY_METRIC_KEY_LIST = [
    'bodyWeightKg',
    'bodyFatPercent',
    'skeletalMuscleKg',
    'bodyWaterLiters',
    'proteinKg',
    'mineralsKg',
    'visceralFatLevel',
    'bmi',
    'bodyWaterPercent',
    'fatMassKg',
    'leanMassKg',
    'bmrKcal',
] as const;

export const INBODY_METRIC_KEYS = INBODY_METRIC_KEY_LIST;

export type InBodyMetricKey = (typeof INBODY_METRIC_KEYS)[number];

export const INBODY_SEGMENT_KEYS = ['rightArm', 'leftArm', 'trunk', 'rightLeg', 'leftLeg'] as const;

export type InBodySegmentKey = (typeof INBODY_SEGMENT_KEYS)[number];

export const INBODY_DASHBOARD_METRIC_KEYS: InBodyMetricKey[] = [
    'bodyFatPercent',
    'skeletalMuscleKg',
    'bodyWaterLiters',
    'visceralFatLevel',
];

export type InBodyScanSource = 'pdf' | 'photo';

export type InBodyNormRange = {
    min: number;
    max: number;
    oneSided: boolean;
    unit: string;
};

export type InBodySegment = {
    leanKg: number;
    leanPctOfStandard: number;
    fatKg: number;
    fatPctOfStandard: number;
};

export type InBodySegmental = Record<InBodySegmentKey, InBodySegment>;

export type InBodyScanMetrics = {
    bodyFatPercent: number;
    skeletalMuscleKg: number;
    visceralFatLevel: number;
    bodyWaterPercent: number;
    bodyWaterLiters?: number | null;
    proteinKg?: number | null;
    mineralsKg?: number | null;
    fatMassKg: number;
    leanMassKg: number;
    bmi: number;
    bmrKcal: number;
};

export type InBodyScanData = InBodyScanMetrics & {
    dateKey: string;
    bodyWeightKg: number;
    source: InBodyScanSource;
    segmental: InBodySegmental;
};

export const INBODY_METRIC_META: Record<InBodyMetricKey, { label: string; unit: string; size: 'full' | 'half' }> = {
    bodyWeightKg: { label: 'Weight', unit: 'kg', size: 'half' },
    bodyFatPercent: { label: 'Body fat', unit: '%', size: 'half' },
    skeletalMuscleKg: { label: 'Muscle', unit: 'kg', size: 'half' },
    bodyWaterLiters: { label: 'Body water', unit: 'L', size: 'half' },
    proteinKg: { label: 'Protein', unit: 'kg', size: 'half' },
    mineralsKg: { label: 'Minerals', unit: 'kg', size: 'half' },
    visceralFatLevel: { label: 'Visceral fat', unit: 'lvl', size: 'half' },
    bmi: { label: 'BMI', unit: '', size: 'half' },
    bodyWaterPercent: { label: 'Body water', unit: '%', size: 'half' },
    fatMassKg: { label: 'Fat mass', unit: 'kg', size: 'half' },
    leanMassKg: { label: 'Lean mass', unit: 'kg', size: 'half' },
    bmrKcal: { label: 'BMR', unit: 'kcal', size: 'half' },
};

export const INBODY_SEGMENT_LABELS: Record<InBodySegmentKey, string> = {
    rightArm: 'R. arm',
    leftArm: 'L. arm',
    trunk: 'Trunk',
    rightLeg: 'R. leg',
    leftLeg: 'L. leg',
};

export const INBODY_SEGMENT_DISPLAY_ORDER: InBodySegmentKey[] = ['leftArm', 'rightArm', 'trunk', 'leftLeg', 'rightLeg'];

export type InBodySegmentMode = 'lean' | 'fat';

const IDEAL_SMM_PERCENT_MALE = 0.47;
const IDEAL_SMM_PERCENT_FEMALE = 0.42;
const IDEAL_BMI_MALE = 22;
const IDEAL_BMI_FEMALE = 21.5;
const PBF_NORM_MALE = { min: 10, max: 20 } as const;
const PBF_NORM_FEMALE = { min: 18, max: 28 } as const;
const BMI_NORM = { min: 18.5, max: 24.99 } as const;
const VISCERAL_FAT_NORM_MAX = 10;
const SMM_NORM_BAND = { min: 0.9, max: 1.1 } as const;
const TBW_NORM_MALE = { min: 0.5, max: 0.65 } as const;
const TBW_NORM_FEMALE = { min: 0.45, max: 0.6 } as const;
const PROTEIN_LBM_NORM = { min: 0.18, max: 0.22 } as const;
const MINERALS_LBM_NORM = { min: 0.062, max: 0.075 } as const;
const TBW_LBM_NORM = { min: 0.67, max: 0.82 } as const;
const LEAN_MASS_NORM_MALE = { min: 0.65, max: 0.8 } as const;
const LEAN_MASS_NORM_FEMALE = { min: 0.6, max: 0.75 } as const;
const SEGMENT_LEAN_NORM = { min: 90, max: 110 } as const;
const SEGMENT_FAT_NORM = { min: 80, max: 160 } as const;

export const INBODY_SEGMENT_LEAN_NORM = SEGMENT_LEAN_NORM;
export const INBODY_SEGMENT_FAT_NORM = SEGMENT_FAT_NORM;

export function idealBmiForSex(sex: 'male' | 'female' | null | undefined): number {
    return sex === 'female' ? IDEAL_BMI_FEMALE : IDEAL_BMI_MALE;
}

export function idealSmmPercentOfWeight(sex: 'male' | 'female' | null | undefined): number {
    return sex === 'female' ? IDEAL_SMM_PERCENT_FEMALE : IDEAL_SMM_PERCENT_MALE;
}

export function inBodyStandardWeightKg(bodyWeightKg: number, bmi: number, sex: 'male' | 'female' | null | undefined): number {
    if (!Number.isFinite(bmi) || bmi <= 0 || !Number.isFinite(bodyWeightKg) || bodyWeightKg <= 0) {
        return bodyWeightKg;
    }
    return (bodyWeightKg / bmi) * idealBmiForSex(sex);
}

export function inBodyStandardSmmKg(bodyWeightKg: number, bmi: number, sex: 'male' | 'female' | null | undefined): number {
    return idealSmmPercentOfWeight(sex) * inBodyStandardWeightKg(bodyWeightKg, bmi, sex);
}

function round1(n: number): number {
    return Math.round(n * 10) / 10;
}

function tbwNormBand(sex: 'male' | 'female' | null | undefined) {
    return sex === 'female' ? TBW_NORM_FEMALE : TBW_NORM_MALE;
}

function pbfNormBand(sex: 'male' | 'female' | null | undefined) {
    return sex === 'female' ? PBF_NORM_FEMALE : PBF_NORM_MALE;
}

function leanMassNormBand(sex: 'male' | 'female' | null | undefined) {
    return sex === 'female' ? LEAN_MASS_NORM_FEMALE : LEAN_MASS_NORM_MALE;
}

function weightFractionNormRange(bodyWeightKg: number, minPct: number, maxPct: number, unit: string): InBodyNormRange | null {
    if (!Number.isFinite(bodyWeightKg) || bodyWeightKg <= 0) return null;
    return {
        min: round1(bodyWeightKg * minPct),
        max: round1(bodyWeightKg * maxPct),
        oneSided: false,
        unit,
    };
}

type InBodyNormScan = Pick<InBodyScanData, 'bmi' | 'leanMassKg' | 'bodyFatPercent'>;

function leanMassKgForNorms(bodyWeightKg: number, scan?: InBodyNormScan | null): number | null {
    const lean = scan?.leanMassKg;
    if (lean != null && Number.isFinite(lean) && lean > 0) return lean;
    const pbf = scan?.bodyFatPercent;
    if (pbf != null && Number.isFinite(pbf) && bodyWeightKg > 0) {
        return round1(bodyWeightKg * (1 - pbf / 100));
    }
    return null;
}

function leanMassFractionNormRange(leanMassKg: number, minPct: number, maxPct: number, unit: string): InBodyNormRange {
    return {
        min: round1(leanMassKg * minPct),
        max: round1(leanMassKg * maxPct),
        oneSided: false,
        unit,
    };
}

export function getInBodyNormRange(
    key: InBodyMetricKey,
    bodyWeightKg: number,
    sex: 'male' | 'female' | null | undefined,
    scan?: InBodyNormScan | null,
): InBodyNormRange | null {
    if (key === 'bodyFatPercent') {
        const band = sex === 'female' ? PBF_NORM_FEMALE : PBF_NORM_MALE;
        return { min: band.min, max: band.max, oneSided: false, unit: '%' };
    }
    if (key === 'visceralFatLevel') {
        return { min: 0, max: VISCERAL_FAT_NORM_MAX, oneSided: true, unit: '' };
    }
    if (key === 'bmi') {
        return { min: BMI_NORM.min, max: BMI_NORM.max, oneSided: false, unit: '' };
    }
    if (key === 'bodyWeightKg') {
        const bmi = scan?.bmi;
        if (bmi == null || !Number.isFinite(bmi) || bmi <= 0) return null;
        return {
            min: round1((bodyWeightKg * BMI_NORM.min) / bmi),
            max: round1((bodyWeightKg * BMI_NORM.max) / bmi),
            oneSided: false,
            unit: 'kg',
        };
    }
    if (key === 'skeletalMuscleKg') {
        const bmi = scan?.bmi;
        if (bmi == null || !Number.isFinite(bmi) || bmi <= 0) return null;
        const standard = inBodyStandardSmmKg(bodyWeightKg, bmi, sex);
        return {
            min: round1(standard * SMM_NORM_BAND.min),
            max: round1(standard * SMM_NORM_BAND.max),
            oneSided: false,
            unit: 'kg',
        };
    }
    if (key === 'proteinKg') {
        const leanMassKg = leanMassKgForNorms(bodyWeightKg, scan);
        if (leanMassKg == null) return null;
        return leanMassFractionNormRange(leanMassKg, PROTEIN_LBM_NORM.min, PROTEIN_LBM_NORM.max, 'kg');
    }
    if (key === 'mineralsKg') {
        const leanMassKg = leanMassKgForNorms(bodyWeightKg, scan);
        if (leanMassKg == null) return null;
        return leanMassFractionNormRange(leanMassKg, MINERALS_LBM_NORM.min, MINERALS_LBM_NORM.max, 'kg');
    }
    if (key === 'bodyWaterLiters') {
        const leanMassKg = leanMassKgForNorms(bodyWeightKg, scan);
        if (leanMassKg == null) return null;
        return leanMassFractionNormRange(leanMassKg, TBW_LBM_NORM.min, TBW_LBM_NORM.max, 'L');
    }
    if (key === 'bodyWaterPercent') {
        const band = tbwNormBand(sex);
        return {
            min: round1(band.min * 100),
            max: round1(band.max * 100),
            oneSided: false,
            unit: '%',
        };
    }
    if (key === 'fatMassKg') {
        const band = pbfNormBand(sex);
        return weightFractionNormRange(bodyWeightKg, band.min / 100, band.max / 100, 'kg');
    }
    if (key === 'leanMassKg') {
        const band = leanMassNormBand(sex);
        return weightFractionNormRange(bodyWeightKg, band.min, band.max, 'kg');
    }
    return null;
}

export type InBodyNormStatus = {
    status: 'normal' | 'watch';
    label: string;
};

export function getInBodyNorm(value: number | null, range: InBodyNormRange | null): InBodyNormStatus | null {
    if (value == null || !range) return null;
    if (value < range.min) return { status: 'watch', label: 'Below normal' };
    if (value <= range.max) return { status: 'normal', label: 'Normal' };
    return { status: 'watch', label: 'Above normal' };
}

export function getInBodySegmentStatus(pctOfStandard: number, kind: 'lean' | 'fat' = 'lean'): InBodyNormStatus {
    const band = kind === 'fat' ? SEGMENT_FAT_NORM : SEGMENT_LEAN_NORM;
    if (pctOfStandard < band.min) return { status: 'watch', label: 'Below normal' };
    if (pctOfStandard <= band.max) return { status: 'normal', label: 'Normal' };
    return { status: 'watch', label: 'Above normal' };
}

export function metricValueFromScan(scan: InBodyScanData, key: InBodyMetricKey): number | null {
    const raw = key === 'bodyWeightKg' ? scan.bodyWeightKg : scan[key];
    if (raw == null || !Number.isFinite(raw)) return null;
    return raw;
}

export function roundInBodyDelta(prev: number | null, next: number | null): number | null {
    if (prev == null || next == null) return null;
    return Math.round((next - prev) * 10) / 10;
}

export const INBODY_SCAN_DATE_MAX_FUTURE_DAYS = 1;
export const INBODY_SCAN_DATE_MAX_PAST_YEARS = 3;

export function isInBodyScanDateOutOfBounds(dateKey: string, todayIsoDate: string): boolean {
    const date = dayjs(dateKey);
    if (!date.isValid()) return true;
    if (date.isAfter(dayjs(todayIsoDate).add(INBODY_SCAN_DATE_MAX_FUTURE_DAYS, 'day'))) return true;
    if (date.isBefore(dayjs(todayIsoDate).subtract(INBODY_SCAN_DATE_MAX_PAST_YEARS, 'year'))) return true;
    return false;
}

export function isInBodyScanDateSuspicious(dateKey: string, todayIsoDate: string, latestExistingDateKey?: string | null): boolean {
    if (isInBodyScanDateOutOfBounds(dateKey, todayIsoDate)) return true;
    if (latestExistingDateKey && dateKey < latestExistingDateKey) return true;
    return false;
}

export function inBodyScanDateYearValues(asOf: Date = new Date()): number[] {
    return rangeValues(asOf.getFullYear() - INBODY_SCAN_DATE_MAX_PAST_YEARS, asOf.getFullYear());
}
